import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { RefreshCwIcon } from 'lucide-react';
import { WelcomeBanner } from '../components/perfect-day/WelcomeBanner';
import { MetricCards } from '../components/perfect-day/MetricCards';
import { PrioritiesPanel } from '../components/perfect-day/PrioritiesPanel';
import { GoalProgress } from '../components/perfect-day/GoalProgress';
import { UpNextPanel } from '../components/perfect-day/UpNextPanel';
import { RecordDrawer } from '../components/perfect-day/RecordDrawer';
import { Toast } from '../components/perfect-day/Toast';
import { dailySummary, loanOfficer } from '../data/perfectDay';
export function PerfectDayPage() {
  const [loading, setLoading] = useState(true);
  const [activeRecordSet, setActiveRecordSet] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const loadDashboard = useCallback(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 700);
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => loadDashboard(), [loadDashboard]);
  const dateLabel = format(new Date(), 'EEEE, MMMM d, yyyy');
  return <main className="w-full bg-[#f7f8fa] px-4 py-5 sm:px-6 sm:py-6 lg:px-8" data-id="element-217" data-mp="3jew21f2lyqgt">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4" data-id="element-218" data-mp="3odgqkh8hzlpf">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-219" data-mp="3odqiiad3s2xr">
          <div data-id="element-220" data-mp="fyov8zowcd3t">
            <p className="text-sm font-semibold text-navy-700" data-id="element-221" data-mp="fy6sdk7qb0mx">
              {loanOfficer.fullName}
            </p>
            <p className="text-xs text-navy-500" data-id="element-222" data-mp="kunqn78n6vdq">
              {loanOfficer.title} · {loanOfficer.branch}
            </p>
          </div>
          <button type="button" onClick={loadDashboard} className="inline-flex h-10 items-center gap-2 rounded-lg border border-navy-200 bg-white px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-223" data-mp="ay17cmvyscdt">
            <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-224" data-mp="2vq4cywkbeo89" />
            Refresh
          </button>
        </div>

        {loading ? <DashboardSkeleton data-id="element-225" data-mp="x9c4j1t9ndmj" /> : <>
            <WelcomeBanner firstName={loanOfficer.firstName} dateLabel={dateLabel} completionPercent={dailySummary.completionPercent} goalsCompleted={dailySummary.goalsCompleted} goalsTotal={dailySummary.goalsTotal} lastSyncedLabel={dailySummary.lastSyncedLabel} data-id="element-226" data-mp="seybh622til1" />

            <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:items-start lg:gap-5" data-id="element-227" data-mp="9gvi6f2hwzhf">
              <div className="order-2 lg:col-span-3 lg:row-start-1" data-id="element-228" data-mp="4iu11zomra8l">
                <MetricCards onOpenRecords={setActiveRecordSet} data-id="element-229" data-mp="4komdf3td4d7" />
              </div>

              <div className="order-1 lg:col-start-3 lg:row-start-2" data-id="element-230" data-mp="23umytyj8sjmd">
                <div className="lg:sticky lg:top-5" data-id="element-231" data-mp="1yxmupt5ydqxw">
                  <UpNextPanel onViewRecord={setActiveRecordSet} onToast={setToast} data-id="element-232" data-mp="27dc1pxlac2cm" />
                </div>
              </div>

              <div className="order-3 flex flex-col gap-4 lg:col-span-2 lg:row-start-2 lg:gap-5" data-id="element-233" data-mp="2bckup08i3w74">
                <PrioritiesPanel onOpenRecords={setActiveRecordSet} data-id="element-234" data-mp="2g807rpyopo2h" />
                <GoalProgress onOpenRecords={setActiveRecordSet} data-id="element-235" data-mp="2l48tjm7e0fim" />
              </div>
            </div>
          </>}
      </div>

      <RecordDrawer recordSetId={activeRecordSet} onClose={() => setActiveRecordSet(null)} data-id="element-236" data-mp="1sg37mw4lkkh7" />
      <Toast message={toast} onDismiss={() => setToast(null)} data-id="element-237" data-mp="1xffsjbbwrhh5" />
    </main>;
}
function DashboardSkeleton() {
  return <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading your Perfect Day" data-id="element-238" data-mp="22fak7h3icb39">
      <div className="rounded-2xl border border-navy-100 bg-white p-6 shadow-card" data-id="element-239" data-mp="38ttci53jjcrw">
        <div className="h-3 w-32 animate-pulse rounded bg-navy-100" data-id="element-240" data-mp="3dscxo3ab8kj7" />
        <div className="mt-3 h-7 w-64 animate-pulse rounded bg-navy-100" data-id="element-241" data-mp="qkp5bgfcgf0d" />
        <div className="mt-3 h-3 w-80 max-w-full animate-pulse rounded bg-navy-50" data-id="element-242" data-mp="qmteodw0i681" />
        <div className="mt-6 h-3 w-full animate-pulse rounded-full bg-navy-100" data-id="element-243" data-mp="lnynanjzb9cb" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-244" data-mp="zmydhzn53wal">
        {[0, 1, 2, 3].map(index => <div key={index} className="rounded-2xl border border-navy-100 bg-white p-4 shadow-card" data-id="element-245" data-mp="uowrb5uf3guw">
            <div className="h-3 w-24 animate-pulse rounded bg-navy-100" data-id="element-246" data-mp="14ljdpz2e6fz1" />
            <div className="mt-3 h-8 w-20 animate-pulse rounded bg-navy-100" data-id="element-247" data-mp="1ic4vfkg40jvc" />
            <div className="mt-3 h-3 w-32 animate-pulse rounded bg-navy-50" data-id="element-248" data-mp="1ie8wzvqjd6sg" />
          </div>)}
      </div>
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:gap-5" data-id="element-249" data-mp="1sac1374y7fi1">
        <div className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card lg:col-span-2" data-id="element-250" data-mp="1s87yqtmp6ojv">
          <div className="h-4 w-40 animate-pulse rounded bg-navy-100" data-id="element-251" data-mp="1n94sx6yw8eau" />
          <div className="mt-5 space-y-4" data-id="element-252" data-mp="26ycbrtuz62pk">
            {[0, 1, 2, 3, 4].map(index => <div key={index} className="flex items-center gap-3" data-id="element-253" data-mp="2bv2piajb4m7t">
                <div className="h-10 w-10 animate-pulse rounded-xl bg-navy-100" data-id="element-254" data-mp="1wz3k57nl106d" />
                <div className="flex-1" data-id="element-255" data-mp="21xy3capgoqr0">
                  <div className="h-3 w-2/5 animate-pulse rounded bg-navy-100" data-id="element-256" data-mp="21wwtg9k6maav" />
                  <div className="mt-2 h-3 w-3/5 animate-pulse rounded bg-navy-50" data-id="element-257" data-mp="1o600ghmcfr3a" />
                </div>
              </div>)}
          </div>
        </div>
        <div className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-258" data-mp="1sz2z345z9qqc">
          <div className="h-4 w-24 animate-pulse rounded bg-navy-100" data-id="element-259" data-mp="1e696ch1p3e3q" />
          <div className="mt-4 h-5 w-44 animate-pulse rounded bg-navy-100" data-id="element-260" data-mp="1j88cfyotkp4d" />
          <div className="mt-3 h-3 w-full animate-pulse rounded bg-navy-50" data-id="element-261" data-mp="157fc2xbu6ztc" />
          <div className="mt-6 h-11 w-full animate-pulse rounded-lg bg-navy-100" data-id="element-262" data-mp="1990ypae6asgq" />
        </div>
      </div>
    </div>;
}